import {
  faDownload,
  faMagnifyingGlass,
  faPlus,
  faSliders,
  faTableColumns,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box } from '@mantine/core';
import type { JSX } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';

import { Breadcrumbs, Button, Input, PageHeader, Table, Toolbar } from '@feathr/components';

import partnersColumns from '../columns/partnersColumns';
import PrototypeDoneModal from '../components/PrototypeDoneModal';
import type { ISamplePartner } from '../sampleData';
import { samplePartners } from '../sampleData';
import { buildPrototypeBreadcrumbs } from '../utils/buildBreadcrumbs';

import * as shellStyles from '../Shell/PrototypeShell.css';

function PartnersPage(): JSX.Element {
  const { t } = useTranslation();
  const match = useRouteMatch<{ accountId: string }>('/:accountId/ia-prototype');
  const base = match?.url ?? '';
  const [search, setSearch] = useState<string>('');
  const [isDoneOpen, setDoneOpen] = useState<boolean>(false);

  const breadcrumbItems = buildPrototypeBreadcrumbs(
    base,
    { label: t('Partner Marketing'), defaultPath: '/partner-marketing/invites' },
    t('Partners'),
    '/partner-marketing/partners',
  );

  const columns = useMemo(() => partnersColumns({ base, t }), [base, t]);

  const filtered = useMemo(() => {
    const lower = search.trim().toLowerCase();
    if (!lower) {
      return samplePartners;
    }
    return samplePartners.filter(
      (p) => p.name.toLowerCase().includes(lower) || p.contact.toLowerCase().includes(lower),
    );
  }, [search]);

  const filterElements = [
    <Box key={'search'} w={320}>
      <Input
        isClearable={true}
        onChange={(next?: string): void => setSearch(next ?? '')}
        placeholder={t('Search by name…')}
        prefix={<FontAwesomeIcon icon={faMagnifyingGlass} />}
        type={'text'}
        value={search}
      />
    </Box>,
    <Toolbar align={'right'} key={'filters'}>
      <Button prefix={<FontAwesomeIcon icon={faSliders} />} type={'secondary'}>
        {t('Filters')}
      </Button>
      <Button prefix={<FontAwesomeIcon icon={faTableColumns} />} type={'secondary'}>
        {t('Customize columns')}
      </Button>
    </Toolbar>,
  ];

  const actions = (
    <Toolbar>
      <Button prefix={<FontAwesomeIcon icon={faDownload} />} type={'secondary'}>
        {t('Export as CSV')}
      </Button>
      <Button
        onClick={(): void => setDoneOpen(true)}
        prefix={<FontAwesomeIcon icon={faPlus} />}
        type={'primary'}
      >
        {t('Add partner')}
      </Button>
    </Toolbar>
  );

  return (
    <>
      <PageHeader
        actions={actions}
        BreadcrumbsComponent={<Breadcrumbs breadcrumbItems={breadcrumbItems} />}
        title={t('Partners')}
      />
      <div className={shellStyles.body}>
        <Table<ISamplePartner>
          columns={columns}
          filterElements={filterElements}
          filterLabel={<></>}
          idKey={'id'}
          isPaginated={false}
          items={filtered}
        />
      </div>

      <PrototypeDoneModal
        description={t('The add-partner workflow opens at this point. The prototype ends here.')}
        onClose={(): void => setDoneOpen(false)}
        opened={isDoneOpen}
        title={t('Add partner')}
      />
    </>
  );
}

export default PartnersPage;
